import { getPricingPlan } from '@common/locales';
import {
  YEARLY_PRICE_MULTIPLIER,
  type BillingInterval,
} from '@common/schemas/subscription-plan.schema';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import AuthGuard from '@/components/auth-guard';
import { PlanCard } from '@/components/subscription/PlanCard';
import { useFirebase } from '@/firebase';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useSubscription } from '@/hooks/useSubscription';
import { FeatureFlags, FeatureMetadata, SubscriptionFeature, SubscriptionPlan } from '@/types/subscription';

const pricingSearchSchema = z.object({
  aifriendly: z.string().optional(),
});

export const Route = createFileRoute('/pricing')({
  component: PricingPage,
  validateSearch: (search) => pricingSearchSchema.parse(search),
});

function PricingContent() {
  const { backend } = useFirebase();
  const { user: dbUser, loading: subLoading } = useSubscription();
  const { t } = useTranslation();
  const search = Route.useSearch();
  const resolveResponsive = useResponsiveValue();

  const planId = dbUser?.planId || 'free';
  const subscription = dbUser?.subscription?.current;
  const stripeSubscriptionStatus = subscription?.stripeStatus ?? subscription?.status;
  const hasExistingSubscription = Boolean(
    dbUser?.stripeCustomerId &&
    (
      dbUser.hasActiveSubscription ||
      (
        stripeSubscriptionStatus &&
        stripeSubscriptionStatus !== 'incomplete' &&
        stripeSubscriptionStatus !== 'cancelled' &&
        stripeSubscriptionStatus !== 'canceled' &&
        stripeSubscriptionStatus !== 'expired' &&
        stripeSubscriptionStatus !== 'incomplete_expired'
      )
    )
  );
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [globalFeatures, setGlobalFeatures] = useState<FeatureMetadata[]>([]);
  const [flagsMap, setFlagsMap] = useState<Record<string, FeatureFlags>>({});
  const [activeBillingPlanId, setActiveBillingPlanId] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if secret query param is present OR user is already on Premium
  const showPremium = search.aifriendly !== undefined || planId === 'premium';

  useEffect(() => {
    if (!backend) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        // 1. Fetch Global Features
        const featuresData = await backend.billing.fetchGlobalFeatures();
        setGlobalFeatures(featuresData);

        // 2. Fetch Plans
        // We fetch all plans and filter in-memory to ensure legacy (inactive) 
        // plans remain visible to users currently subscribed to them.
        const allPlans = await backend.billing.fetchSubscriptionPlans();

        // Filter logic: Show active plans OR the user's current plan.
        // Also handle the premium visibility constraint.
        const filteredPlans = allPlans.filter(plan => {
          if (!getPricingPlan(plan.id)) return false;

          const isUserPlan = plan.id === planId;
          const isActive = plan.isActive === true;

          if (!isActive && !isUserPlan) return false;

          // Hide premium unless specifically requested or already owned
          if (plan.id === 'premium' && !showPremium && !isUserPlan) return false;

          return true;
        });

        setPlans(filteredPlans);

        // 3. Fetch Flags for each filtered plan
        const newFlagsMap: Record<string, FeatureFlags> = {};
        for (const plan of filteredPlans) {
          const flags = await backend.billing.fetchPlanFeatureFlags(plan.id);
          newFlagsMap[plan.id] = flags;
        }
        setFlagsMap(newFlagsMap);

      } catch (err) {
        console.error('Error fetching pricing data:', err);
        setError('Failed to load pricing information');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [backend, showPremium, planId]);

  if (loading || subLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ pt: 4, pb: 6, textAlign: 'center' }}>
          <Typography variant="h2" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
            {t('pricing.title')}
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 400, maxWidth: 800, mx: 'auto', px: { md: 10 } }}>
            {t('pricing.description')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', pb: 4 }}>
          <Tabs
            value={billingInterval}
            onChange={(_, value: BillingInterval) => setBillingInterval(value)}
            aria-label={t('pricing.billing.label')}
            sx={{
              minHeight: 44,
              p: 0.5,
              borderRadius: 1,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                minHeight: 36,
                minWidth: 120,
                borderRadius: 0.75,
                textTransform: 'none',
                fontWeight: 700,
              },
              '& .Mui-selected': {
                color: (theme) => `${theme.palette.primary.contrastText} !important`,
                bgcolor: 'primary.main',
              },
            }}
          >
            <Tab value="month" label={t('pricing.billing.monthly')} disabled={activeBillingPlanId !== null} />
            <Tab value="year" label={t('pricing.billing.yearly')} disabled={activeBillingPlanId !== null} />
          </Tabs>
        </Box>
 
        {/* Pricing Cards */}
        <Grid container spacing={4} justifyContent="center" sx={{ pt: 2 }}>
          {(() => {
            const currentBillingInterval = subscription?.billingInterval ?? 'month';
            const currentPlanPrice = (getPricingPlan(planId)?.price ?? 0) *
              (currentBillingInterval === 'year' ? YEARLY_PRICE_MULTIPLIER : 1);
            return plans.map((plan, index) => {
              const configuredPlan = getPricingPlan(plan.id);
              const planFeatureFlags = flagsMap[plan.id] || {};
              const planFeatures: SubscriptionFeature[] = globalFeatures.map(f => ({
                ...f,
                enabled: planFeatureFlags[f.key] ?? false
              }));

              return (
                <Grid
                  size={resolveResponsive({
                    xs: 12,
                    sm: 6,
                    md: plans.length === 4 ? 3 : 4
                  })}
                  key={plan.id}
                >
                  <PlanCard
                    plan={plan}
                    features={planFeatures}
                    billingInterval={billingInterval}
                    isCurrentPlan={planId === plan.id && (
                      configuredPlan?.price === 0 || currentBillingInterval === billingInterval
                    )}
                    currentPlanPrice={currentPlanPrice}
                    isRecommended={index === Math.floor(plans.length / 2)}
                    hasExistingSubscription={hasExistingSubscription}
                    billingFlowLocked={activeBillingPlanId !== null}
                    onBillingFlowStart={() => setActiveBillingPlanId(plan.id)}
                    onBillingFlowEnd={() => {
                      setActiveBillingPlanId(current =>
                        current === plan.id ? null : current
                      );
                    }}
                  />
                </Grid>
              );
            });
          })()}
        </Grid>
 
        {/* Current Subscription Info */}
        {subscription && (
          <Box sx={{ mt: 6, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" color="text.secondary">
              {t('pricing.currentlyOn')}{' '}
              <strong>
                {dbUser?.hasActiveSubscription
                  ? t(`pricing.plans.${planId}.name`)
                  : t('pricing.plans.free.name')}
              </strong>
            </Typography>
 
            {dbUser?.stripeCustomerId && (
              <Button
                variant="outlined"
                onClick={async () => {
                  if (!backend) return;
                  try {
                    const url = await backend.billing.createPortalSession(window.location.href);
                    window.location.href = url;
                  } catch (err) {
                    console.error('Portal error:', err);
                  }
                }}
                sx={{
                  borderColor: 'divider',
                  color: 'text.primary',
                  px: 4,
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.selected' }
                }}
              >
                {t('pricing.manageSubscription')}
              </Button>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default function PricingPage() {
  return (
    <AuthGuard>
      <PricingContent />
    </AuthGuard>
  );
}
